import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { BaseClass, eMenu } from '../../_common/base.common';
import {
  UI_THEME_PRESETS,
  UiThemePreset,
  UsuarioUiThemeInterface
} from '../../_interfaces/ui-theme.interface';
import { AutenticacaoInterface, ImpersonacaoSessaoInterface } from '../../_interfaces/autenticacao.interface';
import { ClienteUsuarioInterface } from '../../_interfaces/cliente.interface';
import { UiThemeService } from '../../_services/ui-theme/ui-theme.service';
import { ImpersonacaoService } from '../../_services/impersonacao/impersonacao.service';
import { UsuarioService } from '../../_services/usuario/usuario.service';
import { Utilitarios } from '../../_common/utilitarios.common';
import {
  DashboardChartColorTheme,
  DashboardChartThemeService
} from '../../report/dashboard/services/dashboard-chart-theme.service';
import { DashboardTableTheme } from '../../report/dashboard/services/dashboard-datatable.service';
import { DashboardTableThemeService } from '../../report/dashboard/services/dashboard-table-theme.service';

@Component({
  moduleId: module.id,
  selector: 'fixedplugin-cmp',
  templateUrl: 'fixedplugin.component.html',
  styleUrls: ['fixedplugin.component.css']
})
export class FixedPluginComponent extends BaseClass implements OnInit, OnDestroy {
  dropdownOpen = false;
  themePresets = UI_THEME_PRESETS;
  theme: UsuarioUiThemeInterface;

  chartThemeOptions = this.chartThemeService.options;
  tableThemeOptions = this.tableThemeService.options;
  chartColorTheme: DashboardChartColorTheme = 'vibrant';
  tableTheme: DashboardTableTheme = 'paper';

  podeEmular = false;
  emulando = false;
  sessaoEmulacao: ImpersonacaoSessaoInterface = null;
  usuariosEmulacao: ClienteUsuarioInterface[] = [];
  filtroUsuario = '';
  usuarioSelecionadoId: number = null;
  carregandoUsuarios = false;
  emulandoUsuario = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private uiThemeService: UiThemeService,
    private chartThemeService: DashboardChartThemeService,
    private tableThemeService: DashboardTableThemeService,
    private impersonacaoService: ImpersonacaoService,
    private usuarioService: UsuarioService,
    private router: Router
  ) {
    super();
    this.theme = this.uiThemeService.getTheme();
    this.chartColorTheme = this.chartThemeService.getActiveTheme();
    this.tableTheme = this.tableThemeService.getActiveTheme();
  }

  ngOnInit(): void {
    this.VerificarPermissao(eMenu.PerguntasBi);
    if (!this.uiThemeService.isLoaded()) {
      this.uiThemeService.loadAndApply().then(theme => {
        this.theme = theme;
      });
    } else {
      this.theme = this.uiThemeService.getTheme();
    }

    this.subscriptions.push(
      this.chartThemeService.changed$.subscribe(theme => {
        this.chartColorTheme = theme;
      }),
      this.tableThemeService.changed$.subscribe(theme => {
        this.tableTheme = theme;
      })
    );

    this.podeEmular = this.impersonacaoService.podeEmular();
    this.sessaoEmulacao = this.impersonacaoService.obterSessao();
    this.emulando = !!this.sessaoEmulacao;
    this.usuarioSelecionadoId = this.sessaoEmulacao ? this.sessaoEmulacao.id : null;

    if (this.podeEmular) {
      this.carregarUsuariosEmulacao();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  get showAskBiShortcut(): boolean {
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    return this.rolePerguntasBi && (path === '/dashboard' || path.indexOf('/dashboard/') === 0);
  }

  openQuestionsBi(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen = false;
    this.router.navigate(['/perguntas-bi']);
  }

  isPresetActive(preset: UiThemePreset): boolean {
    const t = this.theme;
    const p = preset.theme;
    return (t.pageBackground || '').toLowerCase() === (p.pageBackground || '').toLowerCase()
      && (t.cardBackground || '').toLowerCase() === (p.cardBackground || '').toLowerCase()
      && (t.themeMode || 'light') === (p.themeMode || 'light')
      && (t.sidebarBackground || '') === (p.sidebarBackground || '');
  }

  applyPreset(preset: UiThemePreset, event: Event): void {
    event.stopPropagation();
    this.theme = { ...preset.theme };
    this.uiThemeService.updateTheme({ ...preset.theme });
  }

  onAccentColorInput(event: Event): void {
    const hex = (event.target as HTMLInputElement).value;
    this.applyAccentColor(hex, false);
  }

  onAccentColorChange(event: Event): void {
    const hex = (event.target as HTMLInputElement).value;
    this.applyAccentColor(hex, true);
  }

  onAccentHexBlur(event: Event): void {
    event.stopPropagation();
    const raw = (event.target as HTMLInputElement).value.trim();
    const hex = this.normalizeHex(raw);
    if (!hex) {
      (event.target as HTMLInputElement).value = this.theme.accentColor;
      return;
    }
    this.applyAccentColor(hex, true);
  }

  onChartThemeChange(theme: DashboardChartColorTheme): void {
    this.chartThemeService.setActiveTheme(theme);
  }

  onTableThemeChange(theme: DashboardTableTheme): void {
    this.tableThemeService.setActiveTheme(theme);
  }

  resetTheme(event: Event): void {
    event.stopPropagation();
    this.uiThemeService.resetTheme().then(theme => {
      this.theme = theme;
    });
    this.chartThemeService.setActiveTheme('vibrant');
    this.tableThemeService.setActiveTheme('paper');
  }

  get usuariosFiltrados(): ClienteUsuarioInterface[] {
    const termo = (this.filtroUsuario || '').trim().toLowerCase();
    if (!termo) {
      return this.usuariosEmulacao;
    }

    return this.usuariosEmulacao.filter(u =>
      (u.nmCliente || '').toLowerCase().includes(termo)
      || (u.dsLogin || '').toLowerCase().includes(termo)
    );
  }

  emularUsuarioSelecionado(event: Event): void {
    event.stopPropagation();

    if (!this.podeEmular || !this.usuarioSelecionadoId || this.emulandoUsuario) {
      return;
    }

    const usuario = this.usuariosEmulacao.find(u => u.idClienteUsuario === this.usuarioSelecionadoId);
    const rotulo = usuario
      ? `${usuario.nmCliente} (${usuario.dsLogin})`
      : 'este usuário';

    Swal.fire({
      title: 'Emular perfil',
      html: `Você passará a ver o sistema como <strong>${rotulo}</strong>.`,
      type: 'question',
      showCancelButton: true,
      confirmButtonText: 'Emular',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.value) {
        return;
      }

      this.emulandoUsuario = true;
      this.usuarioService.emularUsuario(this.usuarioSelecionadoId).then((data) => {
        this.impersonacaoService.iniciar(<AutenticacaoInterface>data);
      }, e => {
        this.emulandoUsuario = false;
        Swal.fire('Erro!', this.extrairMensagemErro(e), 'error');
      });
    });
  }

  encerrarEmulacao(event: Event): void {
    event.stopPropagation();
    this.impersonacaoService.encerrar();
  }

  private carregarUsuariosEmulacao(): void {
    this.carregandoUsuarios = true;
    const originalId = (new Utilitarios().RetornarAutenticacaoOriginal()
      || new Utilitarios().RetornarLocalStorageAutenticacao())?.id;

    this.usuarioService.selecionarTodos().then((data) => {
      const lista = <ClienteUsuarioInterface[]>(data || []);
      this.usuariosEmulacao = lista
        .filter(u => u && u.flAdmin !== true && u.flAtivo !== false && u.idClienteUsuario !== originalId)
        .sort((a, b) => (a.nmCliente || '').localeCompare(b.nmCliente || '', 'pt-BR'));
      this.carregandoUsuarios = false;
    }, () => {
      this.carregandoUsuarios = false;
    });
  }

  private extrairMensagemErro(e: any): string {
    const err = e as HttpErrorResponse;
    if (typeof err?.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err?.error?.message) {
      return err.error.message;
    }
    return err?.message || 'Não foi possível emular o usuário.';
  }

  private applyAccentColor(hex: string, persist: boolean): void {
    const normalized = this.normalizeHex(hex);
    if (!normalized) {
      return;
    }

    this.theme.accentColor = normalized;
    this.theme.tableHeaderBackground = 'accent';
    this.theme.tableHeaderText = '#ffffff';
    this.uiThemeService.updateTheme({
      accentColor: normalized,
      tableHeaderBackground: 'accent',
      tableHeaderText: '#ffffff'
    }, persist);
  }

  private normalizeHex(value: string): string | null {
    if (!value) {
      return null;
    }
    let v = value.trim();
    if (!v.startsWith('#')) {
      v = `#${v}`;
    }
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      return v.toLowerCase();
    }
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return null;
  }
}
